import { eur } from "@/lib/api";
import { Check, Clock, ChefHat, Bell, XCircle, Utensils } from "lucide-react";

const STEPS = [
  { key: "received", labelKey: "orderReceived", icon: Check },
  { key: "prep", labelKey: "inPreparation", icon: ChefHat },
  { key: "ready", labelKey: "ready", icon: Bell },
  { key: "served", labelKey: "served", icon: Utensils },
];

function stepIndex(status) {
  switch (status) {
    case "NOUVELLE": return 0;
    case "ACCEPTEE": return 0;
    case "EN_PREPARATION": return 1;
    case "PRETE": return 2;
    case "SERVIE": return 4; // all four steps completed
    default: return 0;
  }
}

function stepDesc(key, done, active, t) {
  const on = done || active;
  switch (key) {
    case "received": return t.descReceived;
    case "prep": return on ? t.descPrepActive : t.descPrepPending;
    case "ready": return on ? t.descReadyActive : t.descReadyPending;
    case "served": return done ? t.descServedDone : t.descServedPending;
    default: return "";
  }
}

export default function OrderTracking({ order, t, theme = { primary: "#123026", gold: "#C69C4A" } }) {
  const refused = order.status === "REFUSEE";
  const served = order.status === "SERVIE";
  const idx = stepIndex(order.status);
  const items = order.items || [];
  const kitchen = items.filter((it) => (it.station || "kitchen") === "kitchen");
  const service = items.filter((it) => (it.station || "kitchen") !== "kitchen");
  const mixed = kitchen.length > 0 && service.length > 0 && !refused && !served && order.status !== "NOUVELLE";
  const kReady = kitchen.length > 0 && kitchen.every((it) => ["ready", "served"].includes(it.item_status));
  const sReady = service.length > 0 && service.every((it) => ["ready", "served"].includes(it.item_status));
  const mixedText = mixed && order.status !== "PRETE" ? (sReady && !kReady ? t.mixedDrinksReady : kReady && !sReady ? t.mixedKitchenReady : t.mixedServicePending) : null;

  const statusText = () => {
    switch (order.status) {
      case "NOUVELLE": return t.trackingHint;
      case "ACCEPTEE": return t.acceptedText || "Commande acceptée, transmise en cuisine.";
      case "EN_PREPARATION": return t.inPrepText;
      case "PRETE": return t.readyText;
      case "SERVIE": return t.servedText;
      case "REFUSEE": return order.refused_by === "client" ? t.cancelledByClientText : t.refusedText;
      default: return t.trackingHint;
    }
  };

  return (
    <div data-testid={`order-tracking-${order.id}`} className="bg-white border border-[#EAE5DC] rounded-3xl p-5 shadow-[0_4px_16px_rgba(18,48,38,0.03)] animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p data-testid="order-number" className="font-serif-display text-xl font-bold text-[#123026] leading-none">{order.order_code || order.order_number}{order.is_addition && <span data-testid="order-addition-badge" className="ml-2 align-middle rounded-full bg-[#C69C4A] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">{t.additionBadge}</span>}</p>
          <p className="text-[11px] text-[#8A918D] uppercase tracking-wider mt-1">{t.table} {order.table_number}</p>
        </div>
        <div className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${refused ? "bg-[#F3EFE7]" : "bg-[#EBF2EE]"}`}>
          {refused ? <XCircle className="w-6 h-6 text-[#8A918D]" /> : served ? <Utensils className="w-6 h-6 text-[#123026]" /> : <Check className="w-6 h-6 text-[#123026]" />}
        </div>
      </div>

      {/* Status banner */}
      <div data-testid="order-status-banner" className={`rounded-2xl px-4 py-3 mb-6 text-center text-sm font-semibold ${refused ? "bg-[#F3EFE7] text-[#123026]" : "text-white"}`} style={refused ? undefined : { backgroundColor: theme.primary }}>
        {statusText()}
      </div>
      {mixedText && <p data-testid="order-mixed-hint" className="-mt-4 mb-6 text-center text-xs text-[#5C615E]">{mixedText}</p>}

      {/* Stepper */}
      {!refused && (
        <div className="relative mb-6">
          {STEPS.map((step, i) => {
            const done = i < idx;
            const active = i === idx;
            const Icon = step.icon;
            const isLast = i === STEPS.length - 1;
            return (
              <div key={step.key} data-testid={`tracking-step-${step.key}`} className="flex items-start gap-4 pb-6 last:pb-0 relative">
                {!isLast && (
                  <div className={`absolute left-[19px] top-10 w-0.5 h-[calc(100%-1rem)] transition-colors ${done ? "bg-[#123026]" : "bg-[#EAE5DC]"}`} />
                )}
                <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 z-10 transition-all ${
                  done ? "bg-[#123026] text-white" : active ? "bg-[#C69C4A] text-white animate-pulse-ring" : "bg-white border border-[#EAE5DC] text-[#C9CFC9]"
                }`}>
                  {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <div className="pt-1.5">
                  <p className={`font-semibold leading-tight ${done || active ? "text-[#123026]" : "text-[#C9CFC9]"}`}>{t[step.labelKey]}</p>
                  <p className={`text-xs mt-0.5 leading-snug ${active ? "text-[#C69C4A] font-medium" : done ? "text-[#5C615E]" : "text-[#C9CFC9]"}`}>
                    {stepDesc(step.key, done, active, t)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Items summary */}
      <div className="rounded-2xl bg-[#FBF9F5] border border-[#EAE5DC] p-4">
        {order.items.map((i, k) => (
          <div key={k} className="flex justify-between py-1 text-sm">
            <span className="text-[#123026]"><span className="font-semibold">{i.quantity}×</span> {i.name}</span>
            <span className="font-semibold text-[#123026]">{eur(i.line_total)}</span>
          </div>
        ))}
        <div className="flex justify-between pt-2.5 mt-1.5 border-t border-[#EAE5DC]">
          <span className="font-semibold text-[#123026]">{t.subtotal}</span>
          <span className="font-serif-display font-bold text-[#123026]">{eur(order.total)}</span>
        </div>
      </div>
    </div>
  );
}
